import React, { useMemo, useState } from 'react';
import { parseApiUtc } from '@/utils/apiDate';
import { appDate, appDateTime, appMonthNameYear } from '@/utils/appDateFormat';
import { useTranslation } from 'react-i18next';
import { Check, CheckCircle2, Clock, Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/Card';
import { KsaWpsSubmissionView } from '../../../../models/payroll';

interface Props {
  history: KsaWpsSubmissionView[];
}

interface PeriodGroup {
  key: string;
  year: number;
  month: number;
  rows: KsaWpsSubmissionView[];
}

const groupByPeriod = (rows: KsaWpsSubmissionView[]): PeriodGroup[] => {
  const map = new Map<string, PeriodGroup>();
  for (const r of rows) {
    const key = `${r.year}-${String(r.month).padStart(2, '0')}`;
    let g = map.get(key);
    if (!g) {
      g = { key, year: r.year, month: r.month, rows: [] };
      map.set(key, g);
    }
    g.rows.push(r);
  }
  for (const g of map.values()) {
    g.rows.sort((a, b) => +new Date(b.generatedOn) - +new Date(a.generatedOn));
  }
  return [...map.values()].sort((a, b) => b.key.localeCompare(a.key));
};

type TFn = ReturnType<typeof useTranslation>['t'];

const StatusBadge: React.FC<{ status: KsaWpsSubmissionView['status']; t: TFn }> = ({ status, t }) => {
  if (status === 'Uploaded') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle2 className="w-3 h-3" />
        {t('payroll.complianceCenter.ksaWps.statusUploaded', 'Uploaded')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
      <Clock className="w-3 h-3" />
      {t('payroll.complianceCenter.ksaWps.statusGenerated', 'Generated')}
    </span>
  );
};

const HashBlock: React.FC<{ value: string; t: TFn }> = ({ value, t }) => {
  const [copied, setCopied] = useState(false);
  return (
    <span className="inline-flex items-center gap-1 font-mono text-xs">
      <span title={value}>{value.slice(0, 8)}</span>
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground"
        aria-label={t('payroll.complianceCenter.ksaWps.copyHash', 'Copy hash')}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch { /* noop */ }
        }}
      >
        <Copy className="w-3 h-3" />
      </button>
      {copied && <Check className="w-3 h-3 text-emerald-600" aria-label={t('payroll.complianceCenter.ksaWps.copied', 'Copied')} />}
    </span>
  );
};

export const KsaMudadHistory: React.FC<Props> = ({ history }) => {
  const { t } = useTranslation();
  const grouped = useMemo(() => groupByPeriod(history), [history]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  if (history.length === 0) {
    return (
      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold mb-2">
            {t('payroll.complianceCenter.ksaWps.historyTitle', 'Mudad Submission History')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t(
              'payroll.complianceCenter.ksaWps.historyEmpty',
              'No submissions yet. Generate a Mudad file to begin.',
            )}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="font-semibold mb-3">
          {t('payroll.complianceCenter.ksaWps.historyTitle', 'Mudad Submission History')}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm [&_th]:px-3 [&_td]:px-3 first:[&_th]:ps-0 first:[&_td]:ps-0">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="text-start py-2 font-medium w-8" />
                <th className="text-start py-2 font-medium">
                  {t('payroll.complianceCenter.ksaWps.colPeriod', 'Period')}
                </th>
                <th className="text-start py-2 font-medium">
                  {t('payroll.complianceCenter.ksaWps.colStatus', 'Status')}
                </th>
                <th className="text-start py-2 font-medium">
                  {t('payroll.complianceCenter.ksaWps.colEmployees', 'Employees')}
                </th>
                <th className="text-end py-2 font-medium">
                  {t('payroll.complianceCenter.ksaWps.colTotal', 'Total (SAR)')}
                </th>
                <th className="text-start py-2 font-medium">
                  {t('payroll.complianceCenter.ksaWps.colGeneratedOn', 'Generated on')}
                </th>
                <th className="text-start py-2 font-medium">
                  {t('payroll.complianceCenter.ksaWps.colUploadedOn', 'Uploaded on')}
                </th>
                <th className="text-start py-2 font-medium">
                  {t('payroll.complianceCenter.ksaWps.colReference', 'Mudad reference')}
                </th>
                <th className="text-start py-2 font-medium">
                  {t('payroll.complianceCenter.ksaWps.colVersions', 'Versions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((g) => {
                const latest = g.rows[0];
                const isOpen = expanded[g.key] ?? false;
                return (
                  <React.Fragment key={g.key}>
                    <tr
                      className="border-b border-border last:border-0 hover:bg-muted/40 cursor-pointer"
                      onClick={() => setExpanded((prev) => ({ ...prev, [g.key]: !isOpen }))}
                    >
                      <td className="py-2">
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </td>
                      <td className="py-2">
                        {appMonthNameYear(new Date(latest.year, latest.month - 1, 1))}
                      </td>
                      <td className="py-2"><StatusBadge status={latest.status} t={t} /></td>
                      <td className="py-2">{latest.employeeCount}</td>
                      <td className="py-2 text-end font-mono">{(latest.totalAmount ?? 0).toFixed(2)}</td>
                      <td className="py-2">{appDate(parseApiUtc(latest.generatedOn))}</td>
                      <td className="py-2">
                        {latest.uploadedOn
                          ? appDate(parseApiUtc(latest.uploadedOn))
                          : t('payroll.complianceCenter.ksaWps.noReference', '—')}
                      </td>
                      <td className="py-2">
                        {latest.mudadReference ?? t('payroll.complianceCenter.ksaWps.noReference', '—')}
                      </td>
                      <td className="py-2">
                        <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs">
                          {t('payroll.complianceCenter.ksaWps.versionTag', 'v{{count}}', { count: g.rows.length })}
                        </span>
                      </td>
                    </tr>

                    {isOpen && (
                      <tr>
                        <td colSpan={9} className="bg-muted/30 px-3 py-2">
                          <ul className="flex flex-col gap-2">
                            {g.rows.map((r, idx) => (
                              <li
                                key={r.id}
                                className="rounded-md border border-border bg-card px-3 py-2.5 text-xs"
                              >
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-foreground">
                                      {t('payroll.complianceCenter.ksaWps.versionLabel', 'Version {{n}}', {
                                        n: g.rows.length - idx,
                                      })}
                                    </span>
                                    <StatusBadge status={r.status} t={t} />
                                  </div>
                                  <span className="flex items-center gap-1">
                                    <span className="text-muted-foreground">
                                      {t('payroll.complianceCenter.ksaWps.colHash', 'File hash')}:
                                    </span>
                                    <HashBlock value={r.fileHash} t={t} />
                                  </span>
                                </div>
                                <div className="mt-1.5 text-muted-foreground">
                                  {appDateTime(parseApiUtc(r.generatedOn))}
                                  <span className="mx-1.5">·</span>
                                  {r.generatedByName}
                                </div>
                                {r.status === 'Uploaded' && r.mudadReference && (
                                  <div className="mt-1 text-muted-foreground">
                                    {t('payroll.complianceCenter.ksaWps.colReference', 'Mudad reference')}:{' '}
                                    {r.mudadReference}{r.uploadedByName ? ` (${r.uploadedByName})` : ''}
                                  </div>
                                )}
                                {r.note && (
                                  <div className="mt-1 text-muted-foreground">{r.note}</div>
                                )}
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default KsaMudadHistory;
